import type React from 'react';
import { useRef, useState } from 'react';
import type { Panel } from '../types/panel';
import type { PanelId } from '../types/panelPlacement';
import { PanelHeaderTitle, PanelTabInput, PanelTabLabel } from './dualPanel.styles';

type PanelTitleProps = {
  panel: Panel;
  onRename: (id: PanelId, title: string) => void;
};

/** The name of a panel that is the only one open. Double-click renames, as a tab does. */
export const PanelTitle: React.FC<PanelTitleProps> = ({ panel, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(panel.title);
  // Blur and Enter both settle the edit; the first one to fire wins.
  const settledRef = useRef(false);

  const startEditing = () => {
    settledRef.current = false;
    setDraft(panel.title);
    setIsEditing(true);
  };

  const finishEditing = (save: boolean) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (save) onRename(panel.id, draft);
    setIsEditing(false);
  };

  return (
    <PanelHeaderTitle onDoubleClick={startEditing} title={isEditing ? undefined : 'Double-click to rename'}>
      {isEditing ? (
        <PanelTabInput
          value={draft}
          autoFocus
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => finishEditing(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') finishEditing(true);
            if (event.key === 'Escape') finishEditing(false);
          }}
        />
      ) : (
        <PanelTabLabel>{panel.title}</PanelTabLabel>
      )}
    </PanelHeaderTitle>
  );
};
